import type {
  PaigeDocumentOrigin,
  PaigeJobMobile,
  PaigeMobileAdded,
  PaigeMobileChannel,
} from './mobile-types';
import { responseWords } from './problem-words';

/**
 * The job's Paige Mobile through the job's channel (`{engineOrigin}` is `/v1/jobs/{id}`): its sheet
 * (`/mobile`), its senders, and where a document came from (`/documents/{id}/origin`). A new invite is
 * the company's route (`/v1/company/mobile-senders/{id}/invite`), reached from the same API origin
 * (docs/engineering/mobile-capture.md, section 13).
 */
export function mobileChannel(
  engineOrigin: string,
  tenant: string | undefined,
): PaigeMobileChannel {
  const api = engineOrigin.replace(/\/jobs\/[^/]+$/, '');
  const json = <T>(path: string, method: string, body?: unknown) =>
    answer<T>(call(tenant, `${engineOrigin}${path}`, method, body));
  return {
    read: () => json<PaigeJobMobile>('/mobile', 'GET'),
    save: (change) => json<PaigeJobMobile>('/mobile', 'PATCH', change),
    add: (people) => json<PaigeMobileAdded>('/mobile/senders', 'POST', { people }),
    remove: (senderId) => json<void>(`/mobile/senders/${senderId}`, 'DELETE'),
    resend: async (senderId) =>
      void (await answer(call(tenant, `${api}/company/mobile-senders/${senderId}/invite`, 'POST'))),
    // 204: the document did not come from a phone, the usual answer. A 404 is kept as "none" only for an
    // API from before 204 (a tab open over a deploy); a 404 for a job that is not the company's is the same blank.
    origin: async (documentId) => {
      const response = await call(tenant, `${engineOrigin}/documents/${documentId}/origin`, 'GET');
      if (response.status === 204 || response.status === 404) return null;
      return answer<PaigeDocumentOrigin>(Promise.resolve(response));
    },
    rescan: (documentId, note) =>
      json<PaigeDocumentOrigin>(`/documents/${documentId}/rescan`, 'POST', {
        ...(note.trim() ? { note: note.trim() } : {}),
      }),
  };
}

/** One request with the company named, a new Idempotency-Key on every change. */
function call(
  tenant: string | undefined,
  url: string,
  method: string,
  body?: unknown,
): Promise<Response> {
  return fetch(url, {
    method,
    credentials: 'include',
    headers: {
      ...(tenant ? { 'Paige-Tenant': tenant } : {}),
      ...(body === undefined ? {} : { 'Content-Type': 'application/json' }),
      ...(method === 'GET' ? {} : { 'Idempotency-Key': crypto.randomUUID() }),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
}

/** The answer's body, or Paige's reason as the error. */
async function answer<T>(pending: Promise<Response>): Promise<T> {
  const response = await pending;
  if (!response.ok) throw new Error(await reasonOf(response));
  return (response.status === 204 ? undefined : await response.json()) as T;
}

function reasonOf(response: Response): Promise<string> {
  return responseWords(response, 'Paige could not do that just now. Try again in a minute.');
}
